import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

/**
 * Tema claro / oscuro.
 *
 * Hay DOS cosas distintas y conviene no mezclarlas:
 *   - `theme`         → lo que el usuario ELIGIÓ: "system" | "light" | "dark".
 *   - `resolvedTheme` → lo que se está PINTANDO: "light" | "dark".
 * Con "system" el resuelto lo dicta el sistema operativo y puede cambiar solo
 * (el usuario cambia el tema del SO, o Android entra en modo noche por
 * horario). Un toggle que solo guardara "claro/oscuro" perdería esa tercera
 * opción, que es la que trae la mayoría de la gente por defecto.
 *
 * El valor se aplica como `data-theme` en <html>, que es de donde cuelgan las
 * dos paletas de `src/index.css`. La MISMA resolución la hace el script inline
 * de `index.html` antes del primer pintado; este provider la continúa. Si se
 * tocan las reglas de acá, hay que tocar aquel también — están duplicadas a
 * propósito (el script no puede importar módulos sin retrasar el pintado).
 */

const STORAGE_KEY = "gps_theme";
const THEMES = ["system", "light", "dark"];

const ThemeContext = createContext(null);

/**
 * Color de la barra de estado (Android / PWA instalada), leído del MISMO token
 * que pinta el fondo de la página en vez de repetirlo como hex acá. Un hex
 * duplicado se desincroniza en cuanto alguien retoca la paleta, y el síntoma
 * —una franja de otro color arriba del todo, solo en el teléfono— no se ve en
 * el navegador de escritorio.
 *
 * Se emite en `rgb(r, g, b)` con comas y no en la sintaxis de espacios: el
 * meta `theme-color` lo parsea el chrome del sistema operativo, que es más
 * viejo que el motor de CSS.
 */
function themeColorFromTokens() {
  const raw = window
    .getComputedStyle(document.documentElement)
    .getPropertyValue("--color-background-dark")
    .trim();
  const ch = raw.split(/[\s,]+/).filter(Boolean);
  return ch.length === 3 ? `rgb(${ch.join(", ")})` : null;
}

/** `matchMedia` no existe en jsdom viejo ni en algunos WebViews. */
function prefersLight() {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  try {
    return window.matchMedia("(prefers-color-scheme: light)").matches;
  } catch {
    return false;
  }
}

function readStored() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return THEMES.includes(raw) ? raw : "system";
  } catch {
    // Safari con cookies bloqueadas lanza con solo LEER localStorage.
    return "system";
  }
}

export function resolveTheme(theme) {
  if (theme === "light" || theme === "dark") return theme;
  return prefersLight() ? "light" : "dark";
}

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(readStored);
  const [systemTheme, setSystemTheme] = useState(() =>
    prefersLight() ? "light" : "dark",
  );

  // Con "system", seguir al SO EN VIVO. Sin este listener el usuario que
  // cambia el tema del sistema con la pestaña abierta se queda con el tema
  // viejo hasta recargar.
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia("(prefers-color-scheme: light)");
    const onChange = (e) => setSystemTheme(e.matches ? "light" : "dark");
    // Safari < 14 no tiene addEventListener en MediaQueryList.
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else mq.removeListener(onChange);
    };
  }, []);

  const resolvedTheme = theme === "system" ? systemTheme : theme;

  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute("data-theme", resolvedTheme);
    const meta = document.querySelector('meta[name="theme-color"]');
    // El color se lee DESPUÉS de estampar el atributo: es el valor ya
    // resuelto de la paleta que acaba de entrar en vigor.
    const color = meta && themeColorFromTokens();
    if (color) meta.setAttribute("content", color);
  }, [resolvedTheme]);

  const setTheme = useCallback((next) => {
    if (!THEMES.includes(next)) return;
    setThemeState(next);
    try {
      // "system" BORRA la clave en vez de guardarla: así el script inline de
      // index.html —que solo entiende "light"/"dark"— cae en su rama de
      // sistema sin tener que replicar la lista de valores válidos.
      if (next === "system") localStorage.removeItem(STORAGE_KEY);
      else localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Sin persistencia el tema vale para esta sesión y ya. No es motivo
      // para no aplicarlo.
    }
  }, []);

  const value = useMemo(
    () => ({ theme, resolvedTheme, setTheme, themes: THEMES }),
    [theme, resolvedTheme, setTheme],
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error("useTheme debe usarse dentro de <ThemeProvider>");
  }
  return ctx;
}
